import React, { useState, useRef } from 'react';
import {
  X,
  UserPlus,
  Shield,
  Check,
  Minus,
  Loader2,
  Search,
  Layers,
  User,
  Mail,
  AtSign,
  Lock,
  Eye,
  EyeOff,
  Hash,
  Sparkles,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Folder,
  FolderOpen
} from 'lucide-react';
import styles from './AdminDashboard.css';
import {
  EXTENSION_GROUPS,
  ALL_EXTENSION_MODULES,
  EXTENSION_CATEGORIES
} from '../../lib/libraries/extensions/extensionModules';

export default function CreateUserModal({ isOpen, onClose, onSubmit, isSubmitting }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    role: 'user',
    numberOfMaxLogins: 1,
    accessibleModules: [],
    gender: 'prefer-not-to-say',
    country: 'India'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [moduleSearch, setModuleSearch] = useState('');
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(() =>
    EXTENSION_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: true }), {})
  );
  const categoryScrollRef = useRef(null);

  if (!isOpen) return null;

  const scrollCategories = (direction) => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: direction === 'left' ? -150 : 150,
        behavior: 'smooth'
      });
    }
  };

  const handleCategoryWheel = (e) => {
    if (categoryScrollRef.current && e.deltaY !== 0) {
      categoryScrollRef.current.scrollLeft += e.deltaY;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => {
      const current = prev.accessibleModules || [];
      const updated = current.includes(moduleId)
        ? current.filter(id => id !== moduleId)
        : [...current, moduleId];
      return {
        ...prev,
        accessibleModules: updated
      };
    });
  };

  const handleSelectAllModules = () => {
    if (formData.accessibleModules.length === ALL_EXTENSION_MODULES.length) {
      setFormData(prev => ({ ...prev, accessibleModules: [] }));
    } else {
      setFormData(prev => ({ ...prev, accessibleModules: ALL_EXTENSION_MODULES.map(m => m.id) }));
    }
  };

  const handleToggleGroup = (group) => {
    const groupModuleIds = group.modules.map(m => m.id);
    const allSelected = groupModuleIds.every(id => formData.accessibleModules.includes(id));

    setFormData(prev => {
      const current = prev.accessibleModules || [];
      let updated;
      if (allSelected) {
        updated = current.filter(id => !groupModuleIds.includes(id));
      } else {
        const toAdd = groupModuleIds.filter(id => !current.includes(id));
        updated = [...current, ...toAdd];
      }
      return { ...prev, accessibleModules: updated };
    });
  };

  const getGroupStatus = (group) => {
    const groupModuleIds = group.modules.map(m => m.id);
    const total = groupModuleIds.length;
    const count = groupModuleIds.filter(id => formData.accessibleModules.includes(id)).length;

    if (count === total) return { state: 'complete', count, total };
    if (count > 0) return { state: 'partial', count, total };
    return { state: 'none', count: 0, total };
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const expandAll = () => {
    setExpandedGroups(EXTENSION_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedGroups({});
  };

  const getCategoryStatus = (category) => {
    if (category === 'All') {
      const total = ALL_EXTENSION_MODULES.length;
      const count = formData.accessibleModules.length;
      if (count === total) return { state: 'complete', count, total };
      if (count > 0) return { state: 'partial', count, total };
      return { state: 'none', count: 0, total };
    }

    const categoryModules = ALL_EXTENSION_MODULES.filter(m => m.category === category);
    const total = categoryModules.length;
    const count = categoryModules.filter(m => formData.accessibleModules.includes(m.id)).length;

    if (count === total) return { state: 'complete', count, total };
    if (count > 0) return { state: 'partial', count, total };
    return { state: 'none', count: 0, total };
  };

  const filteredGroups = EXTENSION_GROUPS.filter(group => {
    const matchesCategory = selectedCategory === 'All' || group.category === selectedCategory;
    if (!matchesCategory) return false;

    if (!moduleSearch.trim()) return true;

    const query = moduleSearch.toLowerCase();
    const matchGroup = group.name.toLowerCase().includes(query) ||
                       group.category.toLowerCase().includes(query) ||
                       group.description.toLowerCase().includes(query);
    const matchModule = group.modules.some(m =>
      m.name.toLowerCase().includes(query) ||
      m.id.toLowerCase().includes(query) ||
      m.description.toLowerCase().includes(query)
    );

    return matchGroup || matchModule;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || !formData.email.trim() || !formData.password.trim()) {
      setError('Please fill in all required fields (Name, Email, Password).');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    onSubmit({
      ...formData,
      numberOfMaxLogins: parseInt(formData.numberOfMaxLogins, 10) || 1
    });
  };

  const assignedCount = formData.accessibleModules.length;
  const totalCount = ALL_EXTENSION_MODULES.length;
  const activeExtensionsCount = EXTENSION_GROUPS.filter(g =>
    g.modules.some(m => formData.accessibleModules.includes(m.id))
  ).length;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modalContent} ${styles.modalSplitContent}`} onClick={(e) => e.stopPropagation()}>
        {/* Pinned Header */}
        <div className={styles.modalHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className={`${styles.statIconWrap} ${styles.statIconActive}`} style={{ width: '38px', height: '38px', borderRadius: '10px' }}>
              <UserPlus size={20} />
            </div>
            <div>
              <h3 className={styles.modalTitle}>Create New User</h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                Create account credentials & configure extension / module access.
              </p>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Split Body */}
          <div className={styles.modalSplitBody}>
            {/* Left Column: Account Details */}
            <div className={styles.modalLeftCol}>
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.35)',
                  color: '#fca5a5',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  lineHeight: '1.4'
                }}>
                  {error}
                </div>
              )}

              {/* Full Name */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <span>Full Name <span style={{ color: '#ef4444' }}>*</span></span>
                </label>
                <div className={styles.inputWithIcon}>
                  <User size={15} className={styles.inputIcon} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Alex Johnson"
                    className={`${styles.formInput} ${styles.formInputWithIcon}`}
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <span>Email Address <span style={{ color: '#ef4444' }}>*</span></span>
                </label>
                <div className={styles.inputWithIcon}>
                  <Mail size={15} className={styles.inputIcon} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g. alex@stemverse.com"
                    className={`${styles.formInput} ${styles.formInputWithIcon}`}
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              {/* Username & Password Row */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <span>Username</span>
                    <span className={styles.formLabelHint}>opt</span>
                  </label>
                  <div className={styles.inputWithIcon}>
                    <AtSign size={14} className={styles.inputIcon} />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="alexj"
                      className={`${styles.formInput} ${styles.formInputWithIcon}`}
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <span>Password <span style={{ color: '#ef4444' }}>*</span></span>
                  </label>
                  <div className={styles.inputWithIcon}>
                    <Lock size={14} className={styles.inputIcon} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min 6 chars"
                      className={`${styles.formInput} ${styles.formInputWithIcon}`}
                      style={{ paddingRight: '32px' }}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        background: 'none',
                        border: 'none',
                        color: '#64748b',
                        cursor: 'pointer',
                        padding: 0,
                        display: 'flex'
                      }}
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Role & Concurrent Logins Row */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>User Role</label>
                  <div className={styles.inputWithIcon}>
                    <Shield size={14} className={styles.inputIcon} />
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className={`${styles.selectInput} ${styles.formInputWithIcon}`}
                      style={{ width: '100%', height: '36px', fontSize: '0.82rem', paddingLeft: '32px' }}
                    >
                      <option value="user">User (Assigned Modules)</option>
                      <option value="admin">Admin (Full Unrestricted)</option>
                    </select>
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    <span>Max Logins</span>
                    <span className={styles.formLabelHint}>&ge; 1</span>
                  </label>
                  <div className={styles.inputWithIcon}>
                    <Hash size={14} className={styles.inputIcon} />
                    <input
                      type="number"
                      name="numberOfMaxLogins"
                      value={formData.numberOfMaxLogins}
                      onChange={handleChange}
                      min="1"
                      className={`${styles.formInput} ${styles.formInputWithIcon}`}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Hierarchical Extensions & Modules */}
            <div className={styles.modalRightCol}>
              <div className={styles.modulesHeaderBar}>
                <div className={styles.modulesCountPill}>
                  <Layers size={14} color="#38bdf8" />
                  <span style={{ color: '#38bdf8', fontWeight: 700 }}>{assignedCount}</span>
                  <span style={{ color: '#94a3b8' }}>/ {totalCount} Modules</span>
                  <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
                    ({activeExtensionsCount} of {EXTENSION_GROUPS.length} Extensions Visible)
                  </span>
                </div>

                {formData.role !== 'admin' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, justifyContent: 'flex-end' }}>
                    {/* Search input */}
                    <div style={{ position: 'relative', width: '160px' }}>
                      <Search size={12} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
                      <input
                        type="text"
                        placeholder="Search extensions & modules..."
                        value={moduleSearch}
                        onChange={(e) => setModuleSearch(e.target.value)}
                        style={{
                          width: '100%',
                          background: 'rgba(15, 23, 42, 0.9)',
                          border: '1px solid rgba(255, 255, 255, 0.12)',
                          borderRadius: '6px',
                          padding: '4px 22px 4px 26px',
                          color: '#fff',
                          fontSize: '0.73rem',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                      {moduleSearch && (
                        <button
                          type="button"
                          onClick={() => setModuleSearch('')}
                          style={{
                            position: 'absolute',
                            right: '6px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {/* Expand / Collapse All */}
                    <button
                      type="button"
                      onClick={() => {
                        const allExpanded = Object.keys(expandedGroups).length === EXTENSION_GROUPS.length;
                        if (allExpanded) collapseAll();
                        else expandAll();
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        color: '#94a3b8',
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        padding: '4px 8px',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                      title="Toggle expand/collapse all"
                    >
                      {Object.keys(expandedGroups).length === EXTENSION_GROUPS.length ? 'Collapse All' : 'Expand All'}
                    </button>

                    {/* Select All */}
                    <button
                      type="button"
                      onClick={handleSelectAllModules}
                      style={{
                        background: 'rgba(56, 189, 248, 0.1)',
                        border: '1px solid rgba(56, 189, 248, 0.25)',
                        color: '#38bdf8',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        padding: '4px 9px',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      {assignedCount === totalCount ? 'Clear All' : 'Select All'}
                    </button>
                  </div>
                )}
              </div>

              {formData.role === 'admin' ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(168, 85, 247, 0.06)',
                  border: '1px dashed rgba(168, 85, 247, 0.3)',
                  borderRadius: '10px',
                  padding: '24px',
                  textAlign: 'center',
                  gap: '12px'
                }}>
                  <Shield size={40} color="#c084fc" />
                  <div style={{ color: '#d8b4fe', fontWeight: 700, fontSize: '1rem' }}>
                    Full Unrestricted Admin Access
                  </div>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#a855f7', maxWidth: '380px', lineHeight: '1.4' }}>
                    Administrators have automatic full access to all {totalCount} system extensions, development environments, and administration consoles.
                  </p>
                </div>
              ) : (
                <>
                  {/* Category Filter Chips Bar */}
                  <div className={styles.categoryBarContainer}>
                    <button
                      type="button"
                      className={styles.categoryScrollBtn}
                      onClick={() => scrollCategories('left')}
                      title="Scroll categories left"
                    >
                      <ChevronLeft size={14} />
                    </button>

                    <div
                      ref={categoryScrollRef}
                      className={styles.categoryChipsWrap}
                      onWheel={handleCategoryWheel}
                    >
                      {['All', ...EXTENSION_CATEGORIES].map(cat => {
                        const isCatActive = selectedCategory === cat;
                        const status = getCategoryStatus(cat);

                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setSelectedCategory(cat)}
                            className={`${styles.categoryChip} ${isCatActive ? styles.categoryChipActive : ''}`}
                          >
                            <span>{cat}</span>
                            
                            {status.state === 'complete' && (
                              <span className={styles.catBadgeComplete}>
                                <Check size={9} />
                                <span>{status.count}/{status.total}</span>
                              </span>
                            )}

                            {status.state === 'partial' && (
                              <span className={styles.catBadgePartial}>
                                <Minus size={9} />
                                <span>{status.count}/{status.total}</span>
                              </span>
                            )}

                            {status.state === 'none' && (
                              <span className={styles.catBadgeNone}>
                                0/{status.total}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      type="button"
                      className={styles.categoryScrollBtn}
                      onClick={() => scrollCategories('right')}
                      title="Scroll categories right"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>

                  {/* Hierarchical Extension List */}
                  <div className={styles.extensionHierarchyWrap}>
                    {filteredGroups.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '32px 16px', color: '#64748b', fontSize: '0.84rem' }}>
                        No extensions or modules matching "{moduleSearch}"
                      </div>
                    ) : (
                      filteredGroups.map(group => {
                        const status = getGroupStatus(group);
                        const isExpanded = moduleSearch ? true : Boolean(expandedGroups[group.id]);

                        const visibleModules = group.modules.filter(m => {
                          if (!moduleSearch.trim()) return true;
                          const query = moduleSearch.toLowerCase();
                          return m.name.toLowerCase().includes(query) ||
                                 m.id.toLowerCase().includes(query) ||
                                 m.description.toLowerCase().includes(query) ||
                                 group.name.toLowerCase().includes(query);
                        });

                        return (
                          <div
                            key={group.id}
                            className={`${styles.extensionAccordion} ${isExpanded ? styles.extensionAccordionExpanded : ''}`}
                          >
                            {/* Extension Header: Checkbox + Name + Status + Chevron */}
                            <div
                              className={styles.extensionAccordionHeader}
                              onClick={() => toggleGroupExpand(group.id)}
                            >
                              {/* Parent Checkbox */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleGroup(group);
                                }}
                                className={`${styles.extensionCheckboxBtn} ${
                                  status.state === 'complete'
                                    ? styles.extensionCheckboxBtnChecked
                                    : status.state === 'partial'
                                      ? styles.extensionCheckboxBtnPartial
                                      : ''
                                }`}
                                title={
                                  status.state === 'complete'
                                    ? 'Uncheck whole extension'
                                    : 'Check whole extension'
                                }
                              >
                                {status.state === 'complete' && <Check size={13} color="#ffffff" strokeWidth={3} />}
                                {status.state === 'partial' && <Minus size={13} color="#ffffff" strokeWidth={3} />}
                              </div>

                              {/* Extension Information */}
                              <div className={styles.extensionInfoWrap}>
                                <div className={styles.extensionTitle}>
                                  <span>{group.name}</span>
                                  {group.category !== group.name && (
                                    <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>
                                      ({group.category})
                                    </span>
                                  )}
                                </div>
                                <div className={styles.extensionSubtitle}>
                                  {group.description}
                                </div>
                              </div>

                              {/* Badge Count */}
                              <div>
                                {status.state === 'complete' && (
                                  <span className={styles.extensionBadgeComplete}>
                                    <Check size={10} />
                                    <span>{status.count}/{status.total} (Active)</span>
                                  </span>
                                )}
                                {status.state === 'partial' && (
                                  <span className={styles.extensionBadgePartial}>
                                    <Minus size={10} />
                                    <span>{status.count}/{status.total} (Partial)</span>
                                  </span>
                                )}
                                {status.state === 'none' && (
                                  <span className={styles.extensionBadgeNone}>
                                    Hidden (0/{status.total})
                                  </span>
                                )}
                              </div>

                              {/* Accordion Chevron Toggle */}
                              <div style={{ color: '#94a3b8', display: 'flex', alignItems: 'center', paddingLeft: '4px' }}>
                                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              </div>
                            </div>

                            {/* Sub-Modules Grid (Visible when Expanded) */}
                            {isExpanded && (
                              <div className={styles.extensionModulesSubGrid}>
                                {visibleModules.map(module => {
                                  const isSelected = formData.accessibleModules.includes(module.id);
                                  return (
                                    <div
                                      key={module.id}
                                      onClick={() => handleModuleToggle(module.id)}
                                      className={`${styles.moduleCard} ${isSelected ? styles.moduleCardSelected : ''}`}
                                    >
                                      <div className={`${styles.moduleCheckboxBox} ${isSelected ? styles.moduleCheckboxBoxChecked : ''}`}>
                                        {isSelected && <Check size={11} color="#ffffff" />}
                                      </div>
                                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                        <span style={{
                                          fontSize: '0.78rem',
                                          fontWeight: 600,
                                          color: isSelected ? '#38bdf8' : '#e2e8f0',
                                          lineHeight: 1.2,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}>
                                          {module.name}
                                        </span>
                                        <span style={{
                                          fontSize: '0.64rem',
                                          color: '#64748b',
                                          marginTop: '2px',
                                          lineHeight: 1.2,
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden'
                                        }}>
                                          {module.description}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pinned Footer (Always in View) */}
          <div className={styles.modalFooter}>
            <span style={{ marginRight: 'auto', fontSize: '0.72rem', color: '#64748b' }}>
              * Required fields
            </span>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  <span>Create User</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
